import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Clock,
  Ban,
  XCircle,
  Filter,
  Receipt,
  Plus,
  Video,
  Home,
  Check,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ViewToggleGroup } from '@/components/cards';
import { CalendarDateControl } from './calendar-date-control';
import { useSchedulingStore } from '@/scheduling/store/scheduling-store';
import {
  APPOINTMENT_STATUS_CODES,
  INVOICE_STATUS_OPTIONS,
  type DateFilter,
} from '@/types/scheduling-api';
import type { OrgLocation } from '@/auth/auth-api';
import { cn } from '@/lib/utils';

/**
 * CalendarSubToolbar — Block
 * Sits between the ProviderStrip and the calendar grid.
 *
 * Filter wiring (Flutter parity):
 *   - Pills (Waiting Room / No Show / Cancelled) toggle a single status
 *     code in `selectedStatusCodes`. Active = blue tint + count badge.
 *     Defaults: WaitingRoom=CheckedIn ON, NoShow ON, Cancelled OFF.
 *   - "Statuses" dropdown is a multi-select over all 10 status codes,
 *     editing the same `selectedStatusCodes` set. Has Select-All / Clear-All.
 *   - "Invoice Statuses" dropdown is multi-select over Draft/Issued/Balanced.
 *     Filter is applied CLIENT-SIDE in scheduling-page (server doesn't
 *     accept invoiceStatus). Mirrors Flutter's applyInvoiceFilter().
 *   - Location buttons are rendered one-per-`user.organizationLocations`
 *     entry — icon comes from the location's `name` ("Office"/"Home"/
 *     "Virtual"/...). Toggling adds/removes its id from `selectedLocationIds`,
 *     which lands directly in the appointments-request `location[]` body.
 *     Empty list = no filter (all locations pass).
 */

interface CalendarSubToolbarProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  calendarView: DateFilter;
  onViewChange: (v: DateFilter) => void;
  /** Views the toggle offers; omitted = every practitioner view. */
  views?: readonly DateFilter[];
  /** The view switch lives on the Practitioners / Devices row instead (Antonin, 13 Oct). */
  hideViewToggle?: boolean;
  /** Date controls moved to the page header (Antonin, 14 Oct). */
  hideDateControls?: boolean;
  /** Counts shown next to the status filter pills. From API response. */
  waitingRoomCount?: number;
  noShowCount?: number;
  cancelledCount?: number;
  /** Status options for the Statuses dropdown — from /api/Appointment/Status. */
  statusOptions?: { code: string; display: string }[];
  /** Practice locations the user has access to — drives the location buttons. */
  organizationLocations?: OrgLocation[];
  /**
   * When true, the provider strip above is currently expanded (showing all
   * rows). The chevron rotates 180° to indicate the collapse direction.
   * Passing `onToggleProviderList` enables the chevron; omit to hide it.
   */
  isProviderListExpanded?: boolean;
  onToggleProviderList?: () => void;
  /** Hide chevron when there is nothing to expand (no overflow). */
  showProviderListToggle?: boolean;
}

function shiftDate(d: Date, deltaDays: number): Date {
  const next = new Date(d);
  next.setDate(d.getDate() + deltaDays);
  return next;
}

/** True when `d` falls on the local calendar's current day. */
function isToday(d: Date): boolean {
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

/**
 * One step of the ‹ › chevrons: a day, a week (Flutter `calendar_tab.dart:365-377`), or a month on the Month view
 * (Antonin, 13 Oct, as in the practitioner calendar: ‹ › move the period shown).
 */
export function stepPeriod(d: Date, view: DateFilter, dir: -1 | 1): Date {
  if (view === 'Month') return new Date(d.getFullYear(), d.getMonth() + dir, 1);
  return shiftDate(d, (view === 'Week' ? 7 : 1) * dir);
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;
const mondayOf = (d: Date) => shiftDate(d, -((d.getDay() + 6) % 7));

/** The period the grid shows, named (practitioner calendar): "12 – 18 Oct 2026", "October 2026", or the long day. */
export function periodLabel(d: Date, view: DateFilter): string | undefined {
  if (view === 'Month') return `${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`;
  if (view !== 'Week') return undefined;
  const a = mondayOf(d);
  const b = shiftDate(a, 6);
  return a.getMonth() === b.getMonth()
    ? `${a.getDate()} – ${b.getDate()} ${MONTHS_SHORT[b.getMonth()]} ${b.getFullYear()}`
    : `${a.getDate()} ${MONTHS_SHORT[a.getMonth()]} – ${b.getDate()} ${MONTHS_SHORT[b.getMonth()]} ${b.getFullYear()}`;
}

/** True when today falls inside the period shown, so Today has nowhere to go. */
export function showsToday(d: Date, view: DateFilter): boolean {
  const now = new Date();
  if (view === 'Month') return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  if (view === 'Week') return mondayOf(d).toDateString() === mondayOf(now).toDateString();
  return isToday(d);
}

/** Quiet neutral controls (practitioner calendar: navigation chrome is neutral; blue is for the page's actions). */
const NAV_ICON = 'grid h-8 w-8 place-items-center rounded-md text-text-secondary hover:bg-grey-100 hover:text-text-primary';
const NAV_TODAY = 'h-8 rounded-md border border-border-default px-3 text-[length:var(--type-body-strong-size)] font-medium leading-[var(--type-body-strong-line)] text-text-primary hover:bg-grey-100 disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent';

/** Map an OrgLocation.name → toolbar icon. Matches Flutter IconHelper.getLocationIcon. */
const LOCATION_ICONS: Record<string, LucideIcon> = {
  office: Plus,
  home: Home,
  virtual: Video,
  block: Ban,
};
function iconForLocation(name: string): LucideIcon {
  return LOCATION_ICONS[name.toLowerCase()] ?? Ban;
}

export function CalendarSubToolbar({
  selectedDate,
  onDateChange,
  calendarView,
  onViewChange,
  views,
  hideViewToggle,
  hideDateControls,
  waitingRoomCount,
  noShowCount,
  cancelledCount,
  statusOptions = [],
  organizationLocations = [],
  isProviderListExpanded = false,
  onToggleProviderList,
  showProviderListToggle = false,
}: CalendarSubToolbarProps) {
  const selectedStatusCodes = useSchedulingStore((s) => s.selectedStatusCodes);
  const toggleStatusCode = useSchedulingStore((s) => s.toggleStatusCode);
  const setSelectedStatusCodes = useSchedulingStore(
    (s) => s.setSelectedStatusCodes,
  );
  const clearStatusCodes = useSchedulingStore((s) => s.clearStatusCodes);

  const selectedInvoiceStatuses = useSchedulingStore(
    (s) => s.selectedInvoiceStatuses,
  );
  const toggleInvoiceStatus = useSchedulingStore((s) => s.toggleInvoiceStatus);
  const setSelectedInvoiceStatuses = useSchedulingStore(
    (s) => s.setSelectedInvoiceStatuses,
  );
  const clearInvoiceStatuses = useSchedulingStore((s) => s.clearInvoiceStatuses);

  const selectedLocationIds = useSchedulingStore((s) => s.selectedLocationIds);
  const toggleLocation = useSchedulingStore((s) => s.toggleLocation);


  // When date controls are hidden (moved to the actor row) and only
  // filters remain, render as a floating pill overlay instead of a bar.

  if (hideDateControls) {
    return null;
  }

  return (
    <div className="relative bg-surface-card border-b border-border-default px-4 py-2 flex items-center gap-3 flex-wrap">
      {/* Provider-strip expand/collapse — anchored to the toolbar's TOP
          border (= the strip's bottom border) so the button straddles the
          dividing line between the provider strip and this toolbar
          (Flutter `Positioned` chevron, calendar_tab.dart:4399-4441). */}
      {onToggleProviderList && showProviderListToggle ? (
        <button
          type="button"
          onClick={onToggleProviderList}
          className="absolute left-1/2 -translate-x-1/2 -top-3 z-10 flex items-center justify-center w-6 h-6 rounded-full bg-surface-card border border-border-default text-brand-primary hover:bg-blue-100 shadow-sm"
          aria-label={isProviderListExpanded ? 'Collapse provider list' : 'Expand provider list'}
          aria-expanded={isProviderListExpanded}
        >
          <ChevronDown
            className={cn(
              'h-4 w-4 transition-transform',
              isProviderListExpanded && 'rotate-180',
            )}
          />
        </button>
      ) : null}

      {/* Left: prev arrow + the shared date control.
          PROD-2408 — the trigger, the month grid and the "Today" footer moved
          into `<CalendarDateControl />` so the Technician Worklist could reuse
          them instead of growing a second date idiom. The step arrows stayed
          here: their STEP is view-dependent (Week moves seven days), which is
          this toolbar's business and not the picker's. */}
      {/* The period, as in the practitioner calendar (Antonin, 13 Oct): its label (the date picker), then ‹ Today ›,
          then the view switch, together on the left and all neutral. Today (PROD-991) is the outline button, disabled
          while the period shown already holds today. */}
      {!hideDateControls && <div data-period-controls className="flex items-center gap-3 whitespace-nowrap">
        <CalendarDateControl value={selectedDate} onChange={onDateChange} label={periodLabel(selectedDate, calendarView)}
          triggerClassName="h-8 px-2 tabular-nums" />
        <span className="flex items-center gap-1" data-period-nav>
          <button
            type="button"
            aria-label={`Previous ${calendarView === 'Week' ? 'week' : calendarView === 'Month' ? 'month' : 'day'}`}
            onClick={() => onDateChange(stepPeriod(selectedDate, calendarView, -1))}
            className={NAV_ICON}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onDateChange(new Date())}
            disabled={showsToday(selectedDate, calendarView)}
            aria-label="Jump to today"
            className={NAV_TODAY}
          >
            Today
          </button>
          <button
            type="button"
            aria-label={`Next ${calendarView === 'Week' ? 'week' : calendarView === 'Month' ? 'month' : 'day'}`}
            onClick={() => onDateChange(stepPeriod(selectedDate, calendarView, 1))}
            className={NAV_ICON}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </span>
        {!hideViewToggle && <ViewToggleGroup value={calendarView} onChange={onViewChange} views={views} />}
      </div>}
      {hideDateControls && !hideViewToggle && <ViewToggleGroup value={calendarView} onChange={onViewChange} views={views} />}

      <div className="flex-1" />

      {/* Status filter pills — quick-toggle shortcuts for the three most common
          codes. These read/write the SAME selectedStatusCodes set as the
          Statuses dropdown (Flutter behavior). */}
      <div className="flex items-center gap-3">
        <FilterPill
          icon={Clock}
          label="Waiting Room"
          count={waitingRoomCount}
          active={selectedStatusCodes.includes('CheckedIn')}
          onClick={() => toggleStatusCode('CheckedIn')}
        />
        <FilterPill
          icon={Ban}
          label="No Show"
          count={noShowCount}
          active={selectedStatusCodes.includes('NoShow')}
          onClick={() => toggleStatusCode('NoShow')}
        />
        <FilterPill
          icon={XCircle}
          label="Cancelled"
          count={cancelledCount}
          active={selectedStatusCodes.includes('Cancelled')}
          onClick={() => toggleStatusCode('Cancelled')}
        />
        <MultiSelectDropdown
          icon={Filter}
          label="Statuses"
          active={selectedStatusCodes.length > 0}
          options={(statusOptions.length > 0
            ? statusOptions
            : APPOINTMENT_STATUS_CODES.map((code) => ({ code, display: code }))
          ).map((s) => ({ value: s.code, label: s.display }))}
          selected={selectedStatusCodes}
          onToggle={toggleStatusCode}
          onSelectAll={() =>
            setSelectedStatusCodes(
              (statusOptions.length > 0
                ? statusOptions.map((s) => s.code)
                : [...APPOINTMENT_STATUS_CODES]),
            )
          }
          onClearAll={clearStatusCodes}
        />
        <MultiSelectDropdown
          icon={Receipt}
          label="Invoice Statuses"
          active={selectedInvoiceStatuses.length > 0}
          options={INVOICE_STATUS_OPTIONS.map((v) => ({ value: v, label: v }))}
          selected={selectedInvoiceStatuses}
          onToggle={toggleInvoiceStatus}
          onSelectAll={() =>
            setSelectedInvoiceStatuses([...INVOICE_STATUS_OPTIONS])
          }
          onClearAll={clearInvoiceStatuses}
        />
      </div>

      {/* Per-location filter buttons — one per organizationLocations entry */}
      <div className="flex items-center gap-1">
        {organizationLocations.map((loc) => {
          const Icon = iconForLocation(loc.name);
          const active = selectedLocationIds.includes(loc.id);
          return (
            <button
              key={loc.id}
              type="button"
              aria-label={`Filter by ${loc.name}`}
              aria-pressed={active}
              onClick={() => toggleLocation(loc.id)}
              className={cn(
                'p-1 rounded transition-colors',
                active
                  ? 'text-brand-primary bg-blue-100 hover:bg-blue-200'
                  : 'text-text-muted hover:text-text-primary hover:bg-grey-100',
              )}
            >
              <Icon className="h-4 w-4" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Filter pill (toggle) ────────────────────────────────────────────────

export function FilterPill({
  icon: Icon,
  label,
  count,
  active,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  count?: number;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'flex h-6 items-center gap-1 rounded-full px-3 text-[length:var(--type-meta-size)] font-medium transition-colors',
        active
          ? 'bg-brand-primary text-text-inverse'
          : 'text-text-secondary hover:text-text-primary',
      )}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
      {typeof count === 'number' && count > 0 && (
        <span
          className={cn(
            'ml-1 text-xs rounded-full px-2 py-0.5 leading-none',
            active
              ? 'bg-brand-primary text-text-inverse'
              : 'bg-status-error text-text-inverse',
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

// ─── Multi-select dropdown ───────────────────────────────────────────────

interface DropdownOption {
  value: string;
  label: string;
}

export function MultiSelectDropdown({
  icon: Icon,
  label,
  active,
  options,
  selected,
  onToggle,
  onSelectAll,
  onClearAll,
  onOpenChange,
}: {
  icon: LucideIcon;
  label: string;
  active: boolean;
  options: DropdownOption[];
  selected: string[];
  onToggle: (value: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  /** Told when the list opens or closes (the floating filter pill stays expanded while a list is open). */
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpenState] = useState(false);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };

  const selectedSet = new Set(selected);

  return (
    // The list is portalled (Popover) so a clipping parent, like the floating filter pill, cannot cut it off.
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <button
            type="button"
            aria-haspopup="listbox"
            className={cn(
              'flex h-6 items-center gap-1 rounded-full px-3 text-[length:var(--type-meta-size)] font-medium transition-colors',
              active
                ? 'text-brand-primary bg-blue-100 font-semibold'
                : 'text-text-muted hover:text-text-primary hover:bg-grey-100',
            )}
          />
        }
      >
        <Icon className="h-4 w-4" />
        <span>{label}</span>
        {active && (
          <span className="ml-1 text-xs rounded-full px-2 py-0.5 leading-none bg-brand-primary text-text-inverse">
            {selected.length}
          </span>
        )}
        <ChevronDown className="h-3 w-3" />
      </PopoverTrigger>

        <PopoverContent align="start" aria-label={label} className="w-[264px] gap-0 p-0 overflow-hidden bg-surface-card border border-border-default shadow-lg ring-0">
          {/* Header — Select all / Clear all */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-border-default text-xs">
            <button
              type="button"
              onClick={onSelectAll}
              className="text-brand-primary hover:underline font-medium"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={onClearAll}
              className="text-text-muted hover:text-text-primary font-medium"
            >
              Clear all
            </button>
          </div>
          <div className="max-h-[280px] overflow-y-auto scrollbar-hide py-1">
            {options.length === 0 ? (
              <div className="px-3 py-2 text-xs text-text-hint">
                No options available
              </div>
            ) : (
              options.map((opt) => {
                const checked = selectedSet.has(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={checked}
                    onClick={() => onToggle(opt.value)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-text-primary hover:bg-grey-100 text-left"
                  >
                    <span
                      className={cn(
                        'h-4 w-4 rounded border flex items-center justify-center flex-shrink-0',
                        checked
                          ? 'bg-brand-primary border-brand-primary text-text-inverse'
                          : 'border-border-strong',
                      )}
                    >
                      {checked && <Check className="h-2 w-2" strokeWidth={3} />}
                    </span>
                    <span className="truncate">{opt.label}</span>
                  </button>
                );
              })
            )}
          </div>
        </PopoverContent>
    </Popover>
  );
}
